import { action, computed, makeObservable, observable, reaction } from "mobx";
import { inject, injectable } from "tsyringe";
import { ViewModel } from "./lib/vvm";
import { NAVIGATION_STORE_TOKEN } from "./stores/navigation";
import type { INavigationStore } from "./stores/navigation";
import { REALTIME_STORE_TOKEN } from "./stores/RealtimeStore";
import type { IRealtimeStore } from "./stores/RealtimeStore";
import {
  SETTINGS_STORE_TOKEN,
  type ISettingsStore,
} from "./stores/SettingsStore";
import {
  BROWSER_NOTIFICATIONS_STORE_TOKEN,
  type IBrowserNotificationsStore,
} from "./stores/BrowserNotificationsStore";
import { Screens } from "./stores/navigation";
import {
  ACCESS_DENIED_EVENT_NAME,
  AUTH_EXPIRED_EVENT_NAME,
  type AccessDeniedEventDetail,
  type AuthExpiredEventDetail,
} from "./utils/authEvents";
import { setupTelegramMiniAppUi } from "./utils/telegramWebApp";

@injectable()
export class AppViewModel extends ViewModel {
  @observable
  accessDeniedMessage: string | null = null;

  private accessDeniedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
    @inject(SETTINGS_STORE_TOKEN)
    private readonly settingsStore: ISettingsStore,
    @inject(REALTIME_STORE_TOKEN)
    private readonly realtime: IRealtimeStore,
    @inject(BROWSER_NOTIFICATIONS_STORE_TOKEN)
    private readonly browserNotificationsStore: IBrowserNotificationsStore,
  ) {
    super();
    makeObservable(this);
  }

  get screen() {
    return this.navigationStore.screen;
  }

  get props() {
    return this.navigationStore.props;
  }

  @computed
  get hasAccessDeniedMessage(): boolean {
    return Boolean(this.accessDeniedMessage);
  }

  @action.bound
  dismissAccessDenied(): void {
    this.accessDeniedMessage = null;
    if (this.accessDeniedTimer) {
      clearTimeout(this.accessDeniedTimer);
      this.accessDeniedTimer = null;
    }
  }

  @action.bound
  private showAccessDeniedMessage(message: string): void {
    const normalized = message.trim();
    if (!normalized) {
      return;
    }

    this.accessDeniedMessage = normalized;
    if (this.accessDeniedTimer) {
      clearTimeout(this.accessDeniedTimer);
      this.accessDeniedTimer = null;
    }

    this.accessDeniedTimer = setTimeout(() => {
      this.dismissAccessDenied();
    }, 5000);
  }

  protected onViewMounted(): void {
    setupTelegramMiniAppUi();
    if (this.settingsStore.realtimeEnabled) {
      this.realtime.start();
    } else {
      this.realtime.stop();
    }
    this.browserNotificationsStore.start();

    this.addDisposer(
      reaction(
        () => this.settingsStore.realtimeEnabled,
        (enabled) => {
          if (enabled) {
            this.realtime.start();
            return;
          }
          this.realtime.stop();
        },
        { fireImmediately: false },
      ),
    );

    if (typeof window !== "undefined") {
      const onAuthExpired = (event: Event): void => {
        const customEvent = event as CustomEvent<AuthExpiredEventDetail>;
        if (customEvent.detail?.reason) {
          this.realtime.stop();
          this.navigationStore.setScreen(Screens.Login);
        }
      };

      const onAccessDenied = (event: Event): void => {
        const customEvent = event as CustomEvent<AccessDeniedEventDetail>;
        const message = customEvent.detail?.message;
        if (message) {
          this.showAccessDeniedMessage(message);
        }
      };

      window.addEventListener(AUTH_EXPIRED_EVENT_NAME, onAuthExpired);
      window.addEventListener(ACCESS_DENIED_EVENT_NAME, onAccessDenied);
      this.addDisposer(() => {
        window.removeEventListener(AUTH_EXPIRED_EVENT_NAME, onAuthExpired);
        window.removeEventListener(ACCESS_DENIED_EVENT_NAME, onAccessDenied);
      });
    }
  }

  protected onViewUnmounted(): void {
    if (this.accessDeniedTimer) {
      clearTimeout(this.accessDeniedTimer);
      this.accessDeniedTimer = null;
    }
    this.browserNotificationsStore.stop();
    this.realtime.stop();
  }
}
